'use client';

import { useEffect, useMemo, useRef } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '@kit/supabase/database';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { ReactionWithUser, UserProfile } from '../types';
import { useCurrentUser } from './use-current-user';

/**
 * Matches a canonical UUID. Optimistic chat messages carry a temporary
 * `temp-<timestamp>` id before the server returns the real UUID; those must
 * never reach a query/subscription filtering on a `uuid` column, or Postgres
 * throws `invalid input syntax for type uuid`.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Load a message's reactions with each reactor's profile.
 *
 * Two queries, not a PostgREST embed: `message_reactions.user_id` references
 * `auth.users`, not `profiles`, so `profiles!message_reactions_user_id_fkey`
 * has no relationship to resolve and PostgREST answers 400 PGRST200. That made
 * every fetch fail and reactions never rendered (BAL-1084). Same batched shape
 * as ReactionService.getReactions: one `.in()` over the distinct user ids.
 *
 * A reaction whose profile cannot be read keeps `user: null` (see
 * ReactionWithUser). A failed profile lookup degrades every reactor to null
 * rather than hiding the reactions themselves, which were read successfully.
 */
const PROFILE_BATCH = 200;

export async function fetchReactionsWithUsers(
  client: SupabaseClient<Database>,
  messageId: string,
): Promise<ReactionWithUser[]> {
  const { data, error } = await client
    .from('message_reactions')
    // Named columns, not '*': a column added later reaches the browser only
    // when this list says so.
    .select('id, message_id, user_id, emoji, created_at')
    .eq('message_id', messageId)
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  const rows = data || [];
  const userIds = [...new Set(rows.map((r) => r.user_id))];

  const profileMap = new Map<string, UserProfile>();
  // Batched, in parallel: one `in()` filter per PROFILE_BATCH ids keeps the
  // request URL bounded however many people reacted, and it is still a fixed
  // number of queries per message, never one per reactor.
  const batches: string[][] = [];
  for (let i = 0; i < userIds.length; i += PROFILE_BATCH) {
    batches.push(userIds.slice(i, i + PROFILE_BATCH));
  }
  const results = await Promise.all(
    batches.map((ids) =>
      client
        .from('profiles')
        .select('id, first_name, last_name, profile_photo_url')
        .in('id', ids),
    ),
  );
  for (const { data: profiles, error: profilesError } of results) {
    // Logged, not thrown: without this an RLS denial reads exactly like a
    // reactor who has no profile.
    if (profilesError) {
      console.warn('[useReactions] reactor profile lookup failed', {
        messageId,
        code: profilesError.code,
        message: profilesError.message,
      });
    }
    for (const profile of profiles || []) {
      profileMap.set(profile.id, profile);
    }
  }

  return rows.map((reaction) => ({
    ...reaction,
    user: profileMap.get(reaction.user_id) ?? null,
  }));
}

interface UseReactionsOptions {
  messageId: string | null;
  realtime?: boolean;
}

/**
 * Hook to get reactions for a message
 */
export function useReactions(options: UseReactionsOptions) {
  const { messageId, realtime = true } = options;
  const client = useSupabase();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);

  // A `temp-...` optimistic id is truthy but not a UUID — treat it as absent so
  // neither the REST query nor the realtime filter sends a non-UUID value.
  const isPersisted = !!messageId && UUID_RE.test(messageId);

  const queryKey = useMemo(() => ['chat-reactions', messageId], [messageId]);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<ReactionWithUser[]> => {
      if (!messageId) return [];

      return fetchReactionsWithUsers(client, messageId);
    },
    enabled: isPersisted,
    staleTime: 60000,
  });

  // Set up realtime subscription
  useEffect(() => {
    if (!realtime || !isPersisted) {
      return;
    }

    if (channelRef.current) {
      client.removeChannel(channelRef.current);
    }

    const channel = client
      .channel(`chat-reactions:${messageId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reactions',
          filter: `message_id=eq.${messageId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        client.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [client, messageId, isPersisted, realtime, queryClient, queryKey]);

  // Group reactions by emoji
  const groupedReactions = useMemo(() => {
    const groups = new Map<string, ReactionWithUser[]>();

    (query.data || []).forEach((reaction) => {
      const existing = groups.get(reaction.emoji) || [];
      groups.set(reaction.emoji, [...existing, reaction]);
    });

    return groups;
  }, [query.data]);

  // Get reaction counts
  const reactionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    groupedReactions.forEach((reactions, emoji) => {
      counts[emoji] = reactions.length;
    });
    return counts;
  }, [groupedReactions]);

  return {
    ...query,
    reactions: query.data || [],
    groupedReactions,
    reactionCounts,
  };
}

/**
 * Hook for toggling reactions on messages
 * Uses the toggle_reaction RPC for atomic operation (single DB call)
 */
export function useToggleReaction() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      emoji,
    }: {
      messageId: string;
      emoji: string;
    }) => {
      // Use RPC for atomic toggle operation (1 DB call instead of 2)
      const { data, error } = await client.rpc('toggle_reaction', {
        p_message_id: messageId,
        p_emoji: emoji.trim(),
      });

      if (error) {
        throw new Error(error.message);
      }

      return { added: !!data };
    },

    onMutate: async ({ messageId }) => {
      await queryClient.cancelQueries({
        queryKey: ['chat-reactions', messageId],
      });

      const previousData = queryClient.getQueryData([
        'chat-reactions',
        messageId,
      ]);

      return { previousData, messageId };
    },

    onError: (_error, { messageId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ['chat-reactions', messageId],
          context.previousData,
        );
      }
    },

    onSuccess: (_data, { messageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['chat-reactions', messageId],
      });
      // Also invalidate messages to update reaction counts
      queryClient.invalidateQueries({
        queryKey: ['chat-messages'],
      });
    },
  });
}

/**
 * Hook to check if current user has reacted with a specific emoji
 */
export function useHasReacted(messageId: string | null, emoji: string) {
  const client = useSupabase();
  const { userId } = useCurrentUser();

  // Optimistic (`temp-...`) ids are truthy but not UUIDs — gate the query so it
  // never filters `message_id` (a uuid column) on a non-UUID value.
  const isPersisted = !!messageId && UUID_RE.test(messageId);

  return useQuery({
    queryKey: ['chat-has-reacted', messageId, emoji, userId],
    queryFn: async (): Promise<boolean> => {
      if (!isPersisted || !userId) return false;

      const { data, error } = await client
        .from('message_reactions')
        .select('id')
        .eq('message_id', messageId)
        .eq('user_id', userId)
        .eq('emoji', emoji.trim())
        .maybeSingle();

      if (error) {
        return false;
      }

      return !!data;
    },
    enabled: isPersisted && !!emoji && !!userId,
    staleTime: 30000,
  });
}
